import { Card } from 'primereact/card'
import { Column as TableColumn } from 'primereact/column'
import { DataTable } from 'primereact/datatable'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { ErrorMessageScreen } from '../common/pure/MessageScreen'
import DateDisplay from '../common/pure/DateDisplay'
import ArchivedStateLabel from '../common/pure/ArchivedStateLabel'
import { Column, Row } from '../common/pure/FlexContainer'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import { TextField } from '../common/pure/input-fields'
import ContextHeader from '../common/ContextHeader'
import Heading from '../common/pure/Heading'
import FullScreenLoading from '../common/FullScreenLoading'
import { getMenuItemArchive, getMenuItemRestore } from '../common/menu-items'
import { useSensorContext } from '../contexts/SensorContext'
import { useUserContext } from '../contexts/UserContext'
import LineGraph from '../graph/LineGraph'
import TablePagination from '../table/TablePagination'
import Calibration from '../../models/Calibration'
import Vector2 from '../../models/Vector2'
import MenuBuilder from '../../tools/MenuBuilder'
import { degreeC, formatDate, ns, toDecimalPlaces } from '../../tools/format'
import t, { _ta } from '../../tools/t'
import { TooltipAvatar } from '../common/ProfileAvatar'

export default function SensorDetailScreen() {
    const { loading, selectedSensor, selectedSensorCalibrations, archiveSensor, restoreSensor } =
        useSensorContext()
    const { me, getUserNameById } = useUserContext()
    const canWriteHardware = me?.hasPermission(undefined, PermissionSuffix.HardwareWrite)

    if (!selectedSensor) {
        if (loading) return <FullScreenLoading />
        return <ErrorMessageScreen />
    }

    const onArchive = async () => {
        if (!confirm(t('sensor_archive_confirm'))) return
        await archiveSensor(selectedSensor.id)
    }

    const onRestore = async () => {
        await restoreSensor(selectedSensor.id)
    }

    const menu = new MenuBuilder()
        .addIf(
            getMenuItemArchive(onArchive, t('sensor_delete')),
            canWriteHardware && !selectedSensor.isArchived
        )
        .addIf(getMenuItemRestore(onRestore), canWriteHardware && selectedSensor.isArchived)
        .toArray()

    const calibrationsNewestFirst = [...selectedSensorCalibrations.items].sort(
        (a, b) => (b.measuredAt?.getTime() ?? 0) - (a.measuredAt?.getTime() ?? 0)
    )
    const calibrationsOldestFirst = [...calibrationsNewestFirst].reverse()

    const points = calibrationsOldestFirst.map(
        (calibration, index) => new Vector2(index, calibration.resultTofNs ?? 0)
    )
    const resultValues = calibrationsOldestFirst
        .map((calibration) => calibration.resultTofNs)
        .filter((value): value is number => typeof value === 'number')

    const min = resultValues.length > 0 ? Math.min(...resultValues) : 0
    const max = resultValues.length > 0 ? Math.max(...resultValues) : 0

    const avg =
        resultValues.length > 0
            ? resultValues.reduce((sum, value) => sum + value, 0) / resultValues.length
            : undefined

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    {selectedSensor.isArchived && (
                        <ArchivedStateLabel onRestore={canWriteHardware ? onRestore : undefined} />
                    )}
                    <ContextHeader menu={menu}>
                        <Heading standalone size={2}>
                            {selectedSensor.serialNo}
                        </Heading>
                    </ContextHeader>
                </ContentColumn>
            </LayoutRowContainer>

            <LayoutRowContainer>
                <ContentColumn columns={1}>
                    <Card title={t('common_details')}>
                        <Column gap={0.75}>
                            <TextField
                                placeholder={t('sensor_field_serial')}
                                value={selectedSensor.serialNo}
                                disabled
                            />
                            <Row gap={0.75}>
                                <TextField
                                    placeholder={t('sensor_field_size')}
                                    value={_ta(selectedSensor, 'size')}
                                    disabled
                                />
                                <TextField
                                    placeholder={t('sensor_field_frequency')}
                                    value={_ta(selectedSensor, 'frequency')}
                                    disabled
                                />
                            </Row>
                            <Row gap={0.75}>
                                <TextField
                                    placeholder={t('sensor_field_times_used')}
                                    value={selectedSensor.timesUsed.toString()}
                                    disabled
                                />
                                <TextField
                                    placeholder={t('life_cycle_field_last_used_summary')}
                                    value={formatDate(selectedSensor.lastUsedAt)}
                                    disabled
                                />
                                <TextField
                                    placeholder={t('life_cycle_field_created_at')}
                                    value={formatDate(selectedSensor.createdAt)}
                                    disabled
                                />
                            </Row>
                        </Column>
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>

            {calibrationsOldestFirst.length > 0 && (
                <LayoutRowContainer>
                    <ContentColumn columns={3}>
                        <Card title={t('sensor_calibration_history')}>
                            <LineGraph min={min} max={max} avg={avg} points={points} />
                        </Card>
                    </ContentColumn>
                </LayoutRowContainer>
            )}

            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <Card title={t('common_calibrations')}>
                        <DataTable
                            value={calibrationsNewestFirst}
                            emptyMessage={t('calibrations_empty')}
                        >
                            <TableColumn
                                header={t('measurement_field_measured_at')}
                                body={(c: Calibration) => (
                                    <Row gap={0.5} alignment="center">
                                        <TooltipAvatar
                                            position="bottom"
                                            id={getUserNameById(c?.measuredBy)}
                                            target={c.id + c.measuredBy}
                                        />

                                        <DateDisplay value={c.measuredAt} target={c.id} />
                                    </Row>
                                )}
                            />

                            <TableColumn
                                header={t('measurement_field_tof')}
                                body={(c: Calibration) => ns(toDecimalPlaces(c.resultTofNs))}
                            />

                            <TableColumn
                                header={t('measurement_field_temperature')}
                                body={(c: Calibration) =>
                                    degreeC(toDecimalPlaces(c.temperatureC, 1))
                                }
                            />

                            <TableColumn
                                header={t('common_calibration_rod')}
                                body={(c: Calibration) => c.calibrationRod?.serialNo}
                            />
                            <TableColumn
                                header={t('common_device')}
                                body={(c: Calibration) => c.device?.serialNo}
                            />
                        </DataTable>
                        <TablePagination data={selectedSensorCalibrations} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
